import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const TermsOfService = () => {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms of Service for Gamification Strategy Generator. Read our terms and conditions for using our AI-powered gamification strategy tool."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/terms-of-service"
        keywords="terms of service, terms and conditions, user agreement, gamification tool terms"
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main className="pt-32 pb-20 px-4">
          <article className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            <header className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
              <p className="text-muted-foreground">Last updated: January 23, 2025</p>
            </header>

            <section className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using the Gamification Strategy Generator service ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Gamification Strategy Generator provides AI-powered gamification strategy recommendations for mobile and web applications. Our Service includes a questionnaire-based assessment that generates personalized gamification strategies, technical specifications, and implementation guidelines.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">3. Payment and Pricing</h2>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>The Service is offered at a one-time payment of $79 USD</li>
                  <li>Payment is processed securely through Stripe</li>
                  <li>All prices are in US Dollars unless otherwise stated</li>
                  <li>You will receive access to your personalized strategy immediately upon successful payment</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">4. Money-Back Guarantee</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We offer a 30-day money-back guarantee. If you are not satisfied with your gamification strategy for any reason, you may request a full refund within 30 days of your purchase. To request a refund, please contact us at support@gamifyme.com with your order details.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">5. Intellectual Property</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>Our Content:</strong> The Service, including its design, features, and content (excluding user-generated content), is owned by Gamification Strategy Generator and is protected by intellectual property laws.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Your Strategy:</strong> The personalized gamification strategy generated for you is yours to use for your own application. You may implement the recommendations in your app without additional licensing fees.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">6. User Responsibilities</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  By using our Service, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Provide accurate and complete information during the assessment</li>
                  <li>Use the Service only for lawful purposes</li>
                  <li>Not share your access credentials with others</li>
                  <li>Not attempt to reverse engineer or copy the Service</li>
                  <li>Not resell or redistribute the generated strategies commercially</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">7. Disclaimer of Warranties</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that our gamification recommendations will achieve specific results for your application. Success depends on various factors including implementation quality, market conditions, and user behavior.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">8. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To the maximum extent permitted by law, Gamification Strategy Generator shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the Service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">9. Service Availability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We strive to maintain continuous availability of our Service but do not guarantee uninterrupted access. We may temporarily suspend the Service for maintenance, updates, or other operational reasons.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">10. Modifications to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will notify users of significant changes by posting the updated Terms on our website. Your continued use of the Service after changes are posted constitutes acceptance of the modified Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">11. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Gamification Strategy Generator operates, without regard to its conflict of law provisions.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-4">12. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For any questions regarding these Terms of Service, please contact us at legal@gamifyme.com.
                </p>
              </div>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;